import { supabase } from '../supabaseClient';
import {
  ALLOWED_NOTIFICATION_TYPES,
  EVENT_NOTIFICATION_TYPES,
  GROUP_NOTIFICATION_TYPES,
  PEOPLE_NOTIFICATION_TYPES,
  getNotificationDestinationFilter,
} from '../utils/notifications';

export async function fetchUnreadNotificationCount(userId) {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .in('type', ALLOWED_NOTIFICATION_TYPES)
    .eq('read', false);
  if (error) throw error;
  return count || 0;
}

// Who did it and which group, so a row can say "Maya joined Portola" instead of the stored sentence.
export const NOTIFICATION_SELECT =
  '*, actor:profiles!notifications_related_user_id_fkey(id, display_name, profile_photo_url), group:groups!notifications_related_group_id_fkey(id, name)';

/** The newest unread notification of the given types, for the toast. */
export async function fetchLatestUnreadNotification(userId, types) {
  const { data, error } = await supabase
    .from('notifications')
    .select(NOTIFICATION_SELECT)
    .eq('user_id', userId)
    .eq('read', false)
    .in('type', types)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  return data?.[0] ?? null;
}

export async function fetchNotificationsList(userId, filter = 'all') {
  if (filter === 'events' && EVENT_NOTIFICATION_TYPES.length === 0) {
    return [];
  }

  let query = supabase
    .from('notifications')
    .select(NOTIFICATION_SELECT)
    .eq('user_id', userId)
    .in('type', ALLOWED_NOTIFICATION_TYPES)
    .order('created_at', { ascending: false });

  if (filter === 'groups') {
    query = query.in('type', GROUP_NOTIFICATION_TYPES);
  } else if (filter === 'events') {
    query = query.in('type', EVENT_NOTIFICATION_TYPES);
  } else if (filter === 'people') {
    query = query.in('type', PEOPLE_NOTIFICATION_TYPES);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export function getNotificationIdsToMark(notification, notifications) {
  const destination = getNotificationDestinationFilter(notification);
  return notifications
    .filter((n) => {
      if (n.read) return false;
      if (destination?.column && destination?.value) {
        return n[destination.column] === destination.value;
      }
      return n.id === notification.id;
    })
    .map((n) => n.id);
}

export async function markNotificationsRead({ userId, notification, notifications }) {
  const idsToMark = getNotificationIdsToMark(notification, notifications);
  if (idsToMark.length === 0) return { markedCount: 0, idsToMark: [] };

  const destination = getNotificationDestinationFilter(notification);
  let query = supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);

  if (destination?.column && destination?.value) {
    query = query.eq(destination.column, destination.value);
  } else {
    query = query.eq('id', notification.id);
  }

  const { error } = await query;
  if (error) throw error;
  return { markedCount: idsToMark.length, idsToMark };
}

export async function markAllNotificationsRead(userId) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) throw error;
}
